import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'

import OrdersService from '../../service/orders.service'

import {
  IOrdersState,
  IOrderStatus,
  IOrdersDashboardSummaryQuery,
  PaginationType,
} from 'dto/orders.dto'
import {
  buildAdminOrdersListQueryFromState,
  IAdminOrdersListQuery,
} from 'handlers/buildAdminOrdersListParams'

const getAdminOrdersList = createAsyncThunk(
  'orders/getAdminOrdersList',
  async (query: IAdminOrdersListQuery | undefined) => {
    try {
      return await OrdersService.getAdminOrdersList(query)
    } catch (error: any) {
      if (error?.data?.message) {
        toast.error(`${error?.data?.message}`)
      } else {
        toast.error(`${error?.statusText}`)
      }
    }
  }
)

/** @deprecated используйте getAdminOrdersList */
const getAllOrders = createAsyncThunk(
  'orders/getAllOrders',
  async (pagination: { rowsPerPage: number; page: number } | undefined) => {
    try {
      const response = await OrdersService.getAdminOrdersList({
        limit: pagination?.rowsPerPage ?? 20,
        offset: pagination
          ? pagination.page * pagination.rowsPerPage
          : 0,
      })
      return response
    } catch (error: any) {
      if (error?.data?.message) {
        toast.error(`${error?.data?.message}`)
      } else {
        toast.error(`${error?.statusText}`)
      }
    }
  }
)

const getDashboardSummary = createAsyncThunk(
  'orders/getDashboardSummary',
  async (query: IOrdersDashboardSummaryQuery | undefined) => {
    try {
      const response = await OrdersService.getDashboardSummary(query)
      return response?.data
    } catch (error: any) {
      if (error?.data?.message) {
        toast.error(`${error?.data?.message}`)
      } else {
        toast.error(`${error?.statusText}`)
      }
    }
  }
)

const getAllOrderStatus = createAsyncThunk(
  'orders/getAllOrderStatus',
  async () => {
    try {
      const response = await OrdersService.getAllOrderStatus()
      return response?.data
    } catch (error: any) {
      if (error?.data?.message) {
        toast.error(`${error?.data?.message}`)
      } else {
        toast.error(`${error?.statusText}`)
      }
    }
  }
)

const createOrderStatus = createAsyncThunk(
  'orders/createOrderStatus',
  async (data: IOrderStatus) => {
    try {
      const response = await OrdersService.createOrderStatus(data)
      toast.success(`Статус создан`)
      return response?.data
    } catch (error: any) {
      if (error?.data?.message) {
        toast.error(`${error?.data?.message}`)
      } else {
        toast.error(`${error?.statusText}`)
      }
    }
  }
)

const editOrderStatus = createAsyncThunk(
  'orders/editOrderStatus',
  async (data: IOrderStatus) => {
    try {
      const response = await OrdersService.editOrderStatus(data)
      toast.success(`Статус отредактирован`)
      return response?.data
    } catch (error: any) {
      if (error?.data?.message) {
        toast.error(`${error?.data?.message}`)
      } else {
        toast.error(`${error?.statusText}`)
      }
    }
  }
)

const deleteOrderStatus = createAsyncThunk(
  'orders/deleteOrderStatus',
  async (id: number | string) => {
    try {
      await OrdersService.deleteOrderStatus(id)
      toast.success(`Статус удалён`)
      return id
    } catch (error: any) {
      if (error?.data?.message) {
        toast.error(`${error?.data?.message}`)
      } else {
        toast.error(`${error?.statusText}`)
      }
    }
  }
)

const editOrder = createAsyncThunk(
  'orders/editOrder',
  async (data: any) => {
    try {
      const response = await OrdersService.editOrder(data)
      toast.success(`Заказ изменён`)
      return response?.data
    } catch (error: any) {
      if (error?.data?.message) {
        toast.error(`${error?.data?.message}`)
      } else {
        toast.error(`${error?.statusText}`)
      }
    }
  }
)

const initialState: IOrdersState = {
  orders: [],
  ordersTotal: 0,
  statuses: [],
  statistic: {
    totalCount: 0,
    todayCount: 0,
    yesterdayCount: 0,
    totalSum: 0,
  },
  filter: {
    status: false,
    pizzeria_id: null,
    status_id: null,
    paymentType: null,
    paymentStatus: null,
  },
  pagination: {
    rowsPerPage: 20,
    page: 0,
  },
  sortBy: 'id',
  sortOrder: 'desc',
}

export const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    setShowOrdersFilter: (state, action: PayloadAction<boolean>): void => {
      state.filter.status = action.payload
    },
    setPage: (state, action: PayloadAction<number>): void => {
      state.pagination.page = action.payload
    },
    setRowsPerPage: (state, action: PayloadAction<PaginationType>): void => {
      state.pagination.rowsPerPage = action.payload
      state.pagination.page = 0
    },
    setFilterPizzeriaId: (
      state,
      action: PayloadAction<number | null>
    ): void => {
      state.filter.pizzeria_id = action.payload
      state.pagination.page = 0
    },
    setFilterStatusId: (state, action: PayloadAction<number | null>): void => {
      state.filter.status_id = action.payload
      state.pagination.page = 0
    },
    setFilterPaymentType: (
      state,
      action: PayloadAction<string | null>
    ): void => {
      state.filter.paymentType = action.payload
      state.pagination.page = 0
    },
    setFilterPaymentStatus: (
      state,
      action: PayloadAction<string | null>
    ): void => {
      state.filter.paymentStatus = action.payload
      state.pagination.page = 0
    },
    setOrdersSort: (
      state,
      action: PayloadAction<{ sortBy: string; sortOrder: 'asc' | 'desc' }>
    ): void => {
      state.sortBy = action.payload.sortBy
      state.sortOrder = action.payload.sortOrder
      state.pagination.page = 0
    },
  },

  extraReducers: builder => {
    builder.addCase(getAdminOrdersList.fulfilled, (state, action) => {
      if (action.payload) {
        state.orders = action.payload.data
        state.ordersTotal = action.payload.total
      }
    })
    builder.addCase(getAllOrders.fulfilled, (state, action) => {
      if (action.payload) {
        state.orders = action.payload.data
        state.ordersTotal = action.payload.total
      }
    })
    builder.addCase(getDashboardSummary.fulfilled, (state, action) => {
      if (action.payload) {
        state.statistic = {
          totalCount: Number(action.payload.totalCount ?? 0),
          todayCount: Number(action.payload.todayCount ?? 0),
          yesterdayCount: Number(action.payload.yesterdayCount ?? 0),
          totalSum: Number(action.payload.totalSum ?? 0),
        }
      }
    })
    builder.addCase(editOrder.fulfilled, (state, action) => {
      if (action.payload) {
        state.orders = state.orders.map(el => {
          if (action?.payload && el.id === action?.payload?.id) {
            return action?.payload
          } else {
            return el
          }
        })
      }
    })
    builder.addCase(getAllOrderStatus.fulfilled, (state, action) => {
      if (action?.payload) {
        state.statuses = action?.payload
      }
    })
    builder.addCase(createOrderStatus.fulfilled, (state, action) => {
      if (action?.payload) {
        state.statuses.push(action?.payload)
      }
    })
    builder.addCase(editOrderStatus.fulfilled, (state, action) => {
      if (action.payload) {
        state.statuses = state.statuses.map(el => {
          if (action?.payload && el.id === action?.payload.id) {
            return action?.payload
          } else {
            return el
          }
        })
      }
    })
    builder.addCase(deleteOrderStatus.fulfilled, (state, action) => {
      if (action?.payload) {
        state.statuses = state.statuses.filter(el => el.id !== action?.payload)
      }
    })
  },
})

export const ordersActions: any = {
  ...ordersSlice.actions,
  getAllOrders,
  getAdminOrdersList,
  getAllOrderStatus,
  createOrderStatus,
  editOrderStatus,
  deleteOrderStatus,
  editOrder,
  getDashboardSummary,
}

export default ordersSlice.reducer
